export default {
  eventsPortal: {
    pageTitle: "Events dashboard",
    pageSubtitle: "Manage your events and moderation processes in one place.",
    pageSubtitleParticipant: "Find events and register to take part.",
    sidebarTitle: "Navigation",
    sidebarSubtitle: "Quick access to dashboard sections",
    navModerationSection: "Moderation",
    sidebarAria: "Events dashboard sidebar",
    navAria: "Events dashboard sections",
    logout: "Log out",
    sectionSubtitle: "Events dashboard section",
    refresh: "Refresh",
    retry: "Retry request",
    loading: "Loading events...",
    loadError: "Could not load events dashboard data. Please try again later.",
    actionError: "Could not complete the action. Please try again.",
    participantsLoadError: "Could not load participant applications.",
    inDevelopmentTitle: "Section is in progress",
    inDevelopmentBody: "Full functionality for this section will be available soon.",
    backToEvents: "Back to all events",
    tabs: {
      overview: "Overview",
      all: "All",
      created: "My events",
      moderation: "Event moderation",
      registrations: "Registration moderation",
      attendance: "Event attendance",
    },
    overview: {
      title: "Dashboard overview",
      subtitle: "Choose a section to continue working with events.",
      createdHint: "Events you manage as an organiser",
      noSections: "No overview sections are available for your account.",
      moderationHint: "Review and publish events",
      registrationsHint: "Review and manage participant registrations",
      statsLoading: "Loading summary…",
      statsLoadError: "Could not load the summary. Please try again later.",
      totalCreated: "Total: {{count}}",
      byStatusTitle: "By status",
      moderationQueue: "Awaiting moderation: {{count}}",
      registrationEvents: "Events where you can manage registrations: {{count}}",
      pendingRegistrationsReview: "Applications awaiting review: {{count}}",
      goToSection: "Open section",
      visitsTitle: "My visits",
      visitsHint: "Events you registered for",
      visitsAppliedTotal: "Applications submitted: {{count}}",
      visitsMetricsTitle: "By application",
      visitsMetrics: {
        applied: "Applied",
        confirmed: "Applications confirmed",
        visited: "Attended",
        pending: "Awaiting confirmation",
      },
      visitsEventsModal: {
        titleApplied: "Events with your application",
        titleConfirmed: "Events with confirmed application",
        titleVisited: "Events you attended",
        titlePending: "Applications awaiting confirmation",
        empty: "No events in this category.",
        close: "Close",
        openList: "Open list: {{label}}, {{count}}",
      },
    },
    registrationCard: {
      registered: "Registered: {{count}}",
      pending: "Awaiting confirmation: {{count}}",
      countsLoading: "Loading registration counts…",
      countsUnavailable: "Could not load registration counts.",
    },
    emptyFilters:
      "No events match the selected filters. Adjust filters or clear directions.",
    empty: {
      all: "Nothing found",
      created: "No events found where you are listed as the organiser.",
      moderation: "No events currently awaiting moderation.",
      registrations: "No published events available for registration work.",
    },
    actions: {
      approve: "Approve",
      reject: "Reject",
      cancelEvent: "Cancel event",
      deleteEvent: "Delete event",
      editEvent: "Edit",
      duplicateEvent: "Copy",
      submitForModeration: "Submit for review",
      notAvailable: "This action is unavailable for the current event status",
    },
    actionsHint: {
      cancelInvalidEndTime: "Could not determine end time. Check the event dates",
      cancelEndTimePassed: "Cannot cancel: end time has already passed",
      cancelAvailable: "You can cancel this event",
      cancelUnavailable: "Cancel is unavailable for the current status",
      deleteAvailable: "You can delete this event",
      deleteUnavailable: "Delete is unavailable for the current status",
    },
    participants: {
      sectionTitle: "Participants and applications",
      pendingTitle: "Awaiting approval",
      otherTitle: "All participants",
      pendingEmpty: "No pending applications.",
      otherEmpty: "No approved or cancelled applications yet.",
      totalCount: "Total applications: {{count}}",
      registeredAt: "Submitted: {{date}}",
      toggleAnswers: "Registration answers",
      answerFallback: "Answer",
      loadMore: "Load more",
      approveRegistrationAria: "Approve application for participant {{name}}",
      rejectRegistrationAria: "Reject application for participant {{name}}",
      booleanYes: "Yes",
      booleanNo: "No",
      viewProfile: "Participant card",
      viewProfileAria: "Open participant card: profile and event registration answers",
      markVisited: "Mark attended",
      markVisitedAria: "Mark attendance for participant {{name}}",
      undoCheckIn: "Remove attendance mark",
      undoCheckInAria: "Remove attendance mark for participant {{name}}",
      searchLabel: "Search",
      searchPlaceholder: "Participant name or email",
      statusFilterLabel: "Status",
      statusFilter: {
        all: "All",
        confirmed: "Confirmed",
        visited: "Attended",
      },
    },
    attendance: {
      title: "Event attendance",
      subtitle: "Mark only participants who attended. If someone did not come, leave them unmarked.",
      selectEventHint: "Select an event in the list on the left to mark participant attendance.",
      eventSearchPlaceholder: "Search by title or venue…",
      eventListAria: "Events list for attendance marking",
      selectEvent: "Select an event",
      eventCount: "Events: {{count}}",
      viewEvent: "View event",
      viewEventAria: "View event card “{{title}}”",
      openEvent: "Open event card",
      summaryAria: "Participant status summary",
      summaryConfirmed: "Confirmed: {{count}}",
      summaryVisited: "Attended: {{count}}",
      summaryTotal: "Total: {{count}}",
      participantColumn: "Participant",
      emailColumn: "Email",
      statusColumn: "Status",
      actionsColumn: "Actions",
      participantsListAria: "Event participants list",
      noEvents: "No published events where you can mark attendance.",
      eventLoadError: "Could not load the event.",
      participantsEmpty: "No participants match the selected filters.",
    },
    undoCheckInModal: {
      title: "Remove attendance mark?",
      message:
        "Participant {{name}} will return to the status they had before check-in. The attendance mark will be removed.",
      cancel: "Cancel",
      confirm: "Remove mark",
    },
    participantProfile: {
      title: "Participant profile",
      modalKicker: "Cabinet",
      back: "Back to event",
      backAria: "Go back to previous screen",
      loadError: "Could not load profile.",
      forbidden: "You do not have access to this profile.",
      notFound: "Profile not found.",
      createdAt: "Account created",
      email: "Email",
      phone: "Phone",
      birthDate: "Date of birth",
      bio: "About",
      genderOther: "Other",
      pointsLevel: "Points and level",
      inn: "TIN",
      snils: "SNILS",
      sectionEducation: "Education",
      sectionWork: "Work experience",
      sectionLanguages: "Languages",
      sectionPortfolio: "Portfolio",
      sectionAccounts: "Accounts",
      sectionExtra: "Additional details",
      sectionResponseExtensions: "API response extensions",
      educationLookupInstitution: "Institution in catalog: {{name}}",
      yearsShort: "{{count}} years experience",
      tabProfile: "Profile",
      tabRegistrationAnswers: "Registration answers",
      registrationLoading: "Loading answers…",
      registrationNotFound: "This participant’s application was not found in the list.",
      registrationNoAnswers: "This application has no answers to the registration form.",
      registrationLoadError: "Could not load registration answers.",
    },
    filters: {
      searchPlaceholder: "Search by title or description",
      statusAll: "All statuses",
      statusRegistrationOpen: "Registration open",
      statusFilterAria: "Status filter: {{status}}",
      sortNewest: "Newest first",
      sortUpcoming: "Upcoming first",
      sortTitle: "By title (A-Z)",
      sortMenuAria: "Sort: {{label}}",
    },
    catalog: {
      openEventDetailsAria: "Open event «{{title}}»",
    },
    badges: {
      registration: "Registration",
      start: "Event start",
    },
    rejectModal: {
      title: "Reject event",
      reasonLabel: "Reject reason",
      reasonPlaceholder: "Enter reason for rejecting the event",
      cancel: "Cancel",
      confirm: "Reject",
    },
    rejectRegistrationModal: {
      title: "Reject participant application?",
      reasonLabel: "Comment for the participant (optional)",
      reasonPlaceholder: "Briefly explain why the application was rejected…",
      cancel: "Cancel",
      confirm: "Reject application",
    },
    cancelEventModal: {
      title: "Cancel this event?",
      cancel: "Cancel",
      confirm: "Cancel event",
    },
    deleteEventModal: {
      title: "Delete this event?",
      cancel: "Cancel",
      confirm: "Delete",
    },
    withdrawEventModal: {
      title: "Withdraw this event from moderation?",
      cancel: "Cancel",
      confirm: "Withdraw",
    },
    export: {
      title: "Export to Excel",
      toolbarButton: "Export to Excel",
      toolbarAria: "Open event Excel export settings",
      close: "Close",
      cancel: "Cancel",
      download: "Download",
      loadingCatalog: "Loading export fields…",
      catalogLoadError: "Could not load export fields. Please try again.",
      downloadError: "Could not download the file. Please try again.",
      noSheetsSelected: "Select at least one sheet to export.",
      sheetsTitle: "Sheets",
      sheetEvent: "Event",
      sheetParticipants: "Participants",
      sheetApplications: "Applications",
      eventFieldsTitle: "Event fields",
      registrationFieldsTitle: "Participant and application fields",
      questionAnswers: "Registration question answers",
      questionAnswersHint: "A separate column for each event registration question.",
      selectAll: "Select all",
      clearAll: "Clear all",
      ungrouped: "Other",
    },
    preview: {
      moderationRejectionTitle: "Moderator rejection reason",
      title: "Event preview",
      back: "Back to list",
      questionsTitle: "Registration questions",
      questionsEmpty: "No registration questions have been added.",
      questionType: "Type",
      publicViewLink: "Participant view",
      loadError: "Could not load the event.",
      forbidden: "You do not have access to this event.",
      notFound: "Event not found.",
      withdrawFromModeration: "Withdraw from moderation",
      staffPermissionsNone: "No permissions assigned",
    },
    registrationStatus: {
      "-1": "Open",
      0: "Registered",
      1: "Attended",
      2: "Checked out",
      3: "Cancelled",
      4: "Waiting approval",
      5: "Approved",
      6: "Rejected",
    },
    create: {
      open: "Create event",
      title: "Create event",
      editTitle: "Edit event",
      duplicateTitle: "Copy event",
      close: "Close",
      cancel: "Cancel",
      submit: "Create",
      update: "Save changes",
      saveDraft: "Save",
      publish: "Publish",
      publishPromptTitle: "Do you want to publish this event?",
      publishPromptYes: "Publish",
      publishPromptNo: "Save as draft",
      saving: "Creating...",
      updating: "Saving...",
      error: "Could not create event",
      duplicateError: "Could not create event copy",
      updateError: "Could not update event",
      selectedImages: "Selected images: {{count}}",
      imageSlotsListAria: "Event images",
      openImagePreviewAria: "Open preview, image {{index}}",
      removeImageAria: "Remove image",
      coverBadge: "Cover",
      setCover: "Set as cover",
      noImagesYet: "Add images using the button above.",
      coverPreviewTitle: "Cover preview",
      coverPreviewDescription: "This is how the image will look on event cards and the event page.",
      coverPreviewHint: "Use a horizontal 16:9 image and keep important details close to the center.",
      lightboxTitle: "Image preview",
      lightboxPrev: "Previous image",
      lightboxNext: "Next image",
      lightboxClose: "Close",
      questionsTitle: "Registration questions",
      addQuestion: "Add question",
      removeQuestion: "Remove question",
      fields: {
        title: "Title",
        description: "Description",
        location: "Location",
        city: "Event city",
        citySearch: "City search",
        startTime: "Event start",
        endTime: "Event end",
        registrationStartTime: "Registration start",
        registrationEndTime: "Registration end",
        date: "Date",
        time: "Time",
        images: "Images",
        chooseImages: "Choose files",
        isPublic: "Public event",
        isPublicDescription:
          "Registrations do not need moderator approval; applications are accepted automatically.",
        questionTitle: "Question text",
        required: "Required question",
        optionsHint: "Enter an answer option",
        addOption: "Add option",
        optionPlaceholder: "Answer option",
        removeOption: "Remove",
        optionsEmpty: "No answer options yet",
        organizerLastName: "Organiser surname",
        organizerFirstName: "Organiser given name",
        organizerEmail: "Organiser email",
        organizerPhone: "Organiser phone",
        direction: "Direction",
        directionPlaceholder: "Select a direction",
      },
      organizerSection: "Organiser contacts",
      socialSection: "Social links",
      socialVk: "VK",
      socialTelegram: "Telegram",
      socialRemove: "Remove",
      socialAddVk: "Add VK",
      socialAddTelegram: "Add Telegram",
      questionTypes: {
        single: "Single choice",
        multi: "Multiple choice",
        text: "Text",
        number: "Number",
        bool: "Yes/No",
      },
      staff: {
        title: "Event staff",
        hint: "Assign colleagues to moderate registration requests and mark participant check-in or check-out.",
        searchLabel: "Find user",
        searchPlaceholder: "At least 2 characters: name, username or email",
        empty: "No staff members added yet.",
        remove: "Remove",
        canModerateRegistrations: "Moderate applications",
        canCheckIn: "Check-in",
        canCheckOut: "Check-out",
      },
      validation: {
        required: "Please fill required fields",
        endAfterStart: "End date must be after start date",
        registrationRange: "Registration end must be after registration start",
        organizerRequired: "Enter organiser given name, surname, email and phone",
        imagesRequired: "Add at least one event image",
        choiceOptionsMin: "Single- and multiple-choice questions need at least two answer options",
        socialUrlHttp: "Social links must start with http:// or https://",
        industryRequired: "Please select an event direction",
      },
    },
  },
  eventsPage: {
    kicker: "What\u2019s on",
    title: "Events",
    subtitle:
      "Lectures, exhibitions, workshops, and other events in the Leninka Art IP ecosystem for authors, researchers, and creative teams",
    registerCta: "Register",
    loadError: "Could not load events. Please try again later.",
    loadMoreError: "Could not load more events.",
    empty: "Nothing found",
    emptyFilters: "No events match the selected filters in the loaded results. Reset filters to \"All\" or scroll to load more.",
    filters: {
      scheduleAll: "All",
      scheduleStartHeading: "Event start",
      scheduleStartAria: "Filter by event start: soon, in progress, or finished",
      registrationAll: "All",
      registrationHeading: "Registration",
      registrationAria: "Filter by registration status: soon, open, or closed",
      registrationSoon: "Soon",
      registrationOpen: "Open",
      registrationClosed: "Closed",
      chipPrefixStart: "Start:",
      chipPrefixRegistration: "Registration:",
      chipsGroupScheduleAria: "Filters by event start",
      chipsGroupRegistrationAria: "Filters by registration",
      comboButtonDefault: "Start & registration",
      comboButtonActive: "Selected: {{count}}",
      comboAria: "Filter by event start and registration",
      industryHeading: "Direction",
      industryAll: "All directions",
      industrySelectedCount: "Selected: {{count}}",
      industryClear: "Clear",
      industryAria: "Filter events by direction",
    },
    searchLabel: "Search events",
    searchPlaceholder: "Search by title or description\u2026",
    statusFilterAria: "Filter by event status",
    statusAll: "All",
    badges: {
      registration: "Registration",
      start: "Event start",
    },
    myRegistrationChip: "Your application",
  },
  participantEventRegistration: {
    status: {
      0: "Registered",
      1: "Checked in",
      2: "Checked out",
      3: "Registration cancelled",
      4: "Awaiting organiser approval",
      5: "Approved",
      6: "Application rejected",
    },
  },
  eventDetail: {
    notFound: "Event not found",
    back: "Back",
    allEvents: "All events",
    about: "About the event",
    loadError: "Could not load the event",
    participation: "Taking part",
    date: "Date",
    time: "Time",
    dateAndTime: "Date and time",
    place: "Venue",
    registerNote: "Registration is free. Seats are limited.",
    share: "Share",
    shareVk: "VK",
    shareTg: "Telegram",
    shareCopy: "Copy link",
    shareCopied: "Link copied",
    galleryTitle: "Photos",
    galleryThumbAria: "Open photo {{index}} of {{total}}",
    galleryLightboxClose: "Close preview",
    galleryLightboxPrev: "Previous photo",
    galleryLightboxNext: "Next photo",
    related: "Other events",
    noRelated: "No other events in the list yet",
    metaSection: "Details and registration",
    direction: "Direction",
    registrationPeriod: "Registration period",
    locationRegion: "City and country",
    organizer: "Organiser",
    organizerEmail: "Organiser email",
    organizerPhone: "Organiser phone",
    modalTitle: "Registration",
    registeredTitle: "Application accepted!",
    registeredEmailHeadline: "Check your inbox",
    registeredEmailBody:
      "You will receive an email with your registration confirmation. Without completing this step, your registration is not considered complete.",
    registeredEventLabel: "Event:",
    submitRegister: "Confirm registration",
    registerSending: "Sending\u2026",
    registerValidation: "Please fill in all required fields",
    registerFailed: "Registration failed",
    questionFallback: "Question",
    selectPlaceholder: "Select an option",
    multiSelectHint: "Select one or more options",
    noQuestionsHint: "No extra registration questions. Use the button below to confirm your participation.",
    alreadyRegisteredMessage:
      "You have already applied for this event. Your application status is shown under Taking part. You do not need to register again.",
    myRegistrationHeadline: "Your application",
    myRegistrationHint: "Application status",
    cancelRegistration: "Cancel registration",
    cancelRegistrationHint: "After cancelling you can apply again if registration is still open.",
    cancelCannotAfterCheckIn: "Cancellation is not available after check-in.",
    cancelConfirmTitle: "Cancel registration?",
    cancelConfirmBody: "Are you sure you want to cancel your participation in this event?",
    cancelConfirmAction: "Yes, cancel",
    cancelConfirmAbort: "No, keep it",
    cancelSending: "Cancelling…",
    cancelFailed: "Could not cancel registration",
    registrationCheckError: "Could not load your application status",
  },
}
